import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog } from "@carepass/ui";
import { memberApi } from "../shared/api";
import { MemberActionBar } from "../shared/MemberLayouts";
import { checkoutUrlForCurrentDevice } from "../shared/checkout-url";
import { Alert, Button, EmptyState, ErrorState, LoadingState, money, navigate } from "../shared/ui";
import { TransactionSettlementSteps } from "./TransactionSettlementSteps";
import { Icon, MemberTopBar } from "./MemberMobileUi";
import { TransactionSettlementSummary } from "./TransactionSettlementSummary";
import { isTransactionPaid, settlementError, useSettlementTransaction } from "./transaction-settlement";
import { transactionPath } from "./transaction-groups";
import { cacheTransactionResult } from "./transaction-cache";
import { rememberTransactionPaymentReturn } from "./transaction-payment-return-context";
import { allowedTransactionAction, transactionActionBlockedMessage } from "./transaction-actions";
import { TransactionActions } from "./TransactionActions";

export function TransactionPaymentPage({ transactionId }: { readonly transactionId: number }) {
  const client = useQueryClient();
  const { query, refresh } = useSettlementTransaction(transactionId);
  const [agreed, setAgreed] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string>();
  const [checked, setChecked] = useState(false);
  const submitting = useRef(false);
  const banks = useQuery({ queryKey: ["member", "banks"], queryFn: memberApi.banks });
  useEffect(() => { setAgreed(false); setConfirming(false); setError(undefined); setChecked(false); }, [transactionId]);
  const payment = useMutation({
    mutationFn: async () => {
      const latest = await refresh();
      if (!latest) throw new Error("최신 결제 상태를 확인하지 못했습니다.");
      if (!["PAYMENT_PENDING", "PAYMENT_FAILED"].includes(latest.status)) return latest;
      if (!allowedTransactionAction(latest, "PAY")) throw new Error(transactionActionBlockedMessage(latest, "PAY"));
      return memberApi.payTransaction(transactionId);
    },
    onSuccess: result => {
      cacheTransactionResult(client, result);
      setConfirming(false);
      setError(undefined);
      const url = !isTransactionPaid(result) && result.status === "PAYMENT_PROCESSING" ? checkoutUrlForCurrentDevice(result) : undefined;
      if (url) { rememberTransactionPaymentReturn(result); window.location.assign(url); }
    },
    onError: async cause => {
      const latest = await refresh();
      setConfirming(false);
      setError(latest && isTransactionPaid(latest) ? undefined : settlementError(cause, "결제 요청을 확인하지 못했습니다. 거래 상태를 확인한 뒤 다시 시도해 주세요."));
    },
    onSettled: () => { submitting.current = false; },
  });
  const reconcile = useMutation({
    mutationFn: () => memberApi.reconcileTransactionPayment(transactionId),
    onSuccess: result => { cacheTransactionResult(client, result); setChecked(result.status === "PAYMENT_PROCESSING"); setError(undefined); },
    onError: cause => setError(settlementError(cause, "결제 결과를 확인하지 못했습니다. 잠시 후 상태를 다시 확인해 주세요.")),
  });
  const reset = useMutation({
    mutationFn: () => memberApi.resetTransactionPayment(transactionId),
    onSuccess: result => { cacheTransactionResult(client, result); setChecked(result.status === "PAYMENT_PROCESSING"); setError(undefined); },
    onError: cause => setError(settlementError(cause, "이전 결제 상태를 확인하지 못했습니다. 다시 확인해 주세요.")),
  });
  if (query.isPending) return <><MemberTopBar title="카드 결제" backTo={transactionPath(`/app/transactions/${transactionId}`)} /><LoadingState label="결제 상태를 확인하고 있습니다." /></>;
  if (!query.data) return <><MemberTopBar title="카드 결제" backTo={transactionPath(`/app/transactions/${transactionId}`)} /><ErrorState error={query.error} onRetry={() => void refresh()} /></>;
  const transaction = query.data;
  const paid = isTransactionPaid(transaction);
  const selectable = Boolean(allowedTransactionAction(transaction, "SELECT_TRANSFER"));
  const pending = transaction.status === "PAYMENT_PROCESSING";
  const unknown = transaction.status === "PAYMENT_UNKNOWN";
  const payable = ["PAYMENT_PENDING", "PAYMENT_FAILED"].includes(transaction.status) && Boolean(allowedTransactionAction(transaction, "PAY"));
  const busy = reconcile.isPending || reset.isPending;
  return <div className="member-settlement settlement-payment">
    <TransactionSettlementSteps transaction={transaction} stage="payment" />
    <div className="settlement-body">
    <TransactionSettlementSummary transaction={transaction} />
    {paid && <Alert persistent variant="success" className="settlement-approved-notice" title={<span role="heading" aria-level={2}>결제를 완료했어요.</span>}>
      {selectable ? "송금을 진행해 주세요." : "결제는 완료되었어요. 2 송금에서 예약과 처리 결과를 확인할 수 있어요."}
    </Alert>}
    {error && <Alert persistent variant="danger" title={pending || unknown ? "결제 결과를 확인하지 못했습니다" : "결제 요청을 처리하지 못했습니다"}>{error}</Alert>}
    {query.isError && <Alert persistent variant="warning" title="최신 상태를 확인하지 못했습니다">새 요청을 진행하기 전에 상태를 다시 확인해 주세요.</Alert>}
    {paid && <MemberActionBar><Button size="large" onClick={() => navigate(transactionPath(`/app/transactions/${transactionId}/transfer`))}>{selectable ? "송금하기" : "송금 상태 보기"}</Button></MemberActionBar>}
    {(pending || unknown) && <>
      <Alert persistent variant="information" title={checked ? "결제 결과가 아직 확정되지 않았습니다" : "카드 결제 결과를 확인하고 있습니다"}>
        {unknown ? "결제 결과가 불명확하여 재결제를 보류하고 있어요. 결제 상태를 확인하거나 고객센터에 문의해 주세요."
          : checked ? "결제 승인 진행 중이거나 결과가 아직 확인되지 않았어요. 결제 상태를 다시 확인해 주세요."
          : "결제창을 닫았다면 다시 결제하기를 눌러 주세요. 미승인 여부를 확인한 뒤 새 결제를 준비해요. 거래 취소도 할 수 있어요."}
      </Alert>
      <Button variant="quiet" disabled={busy} onClick={() => reconcile.mutate()}>결제 상태 확인</Button>
      <MemberActionBar><Button size="large" isLoading={busy}
        onClick={() => { if (unknown) reconcile.mutate(); else reset.mutate(); }}>
        {unknown ? "상태 다시 확인" : "다시 결제하기"}
      </Button></MemberActionBar>
      {checked && <Button variant="quiet" onClick={() => navigate("/app/inquiries")}>고객센터 문의</Button>}
    </>}

    {payable && <>
      {transaction.status === "PAYMENT_FAILED" && <Alert persistent variant="warning" title={transaction.lastErrorCode === "CHECKOUT_RETIRED" ? "이전 결제창을 종료했어요" : "카드 결제가 완료되지 않았습니다"}>{transaction.lastErrorCode === "CHECKOUT_RETIRED" ? "새로 결제하거나 거래를 취소할 수 있어요." : "카드 정보를 확인한 뒤 다시 결제할 수 있어요."}</Alert>}
      <div className="settlement-card-benefits"><Icon name="card" size={18} /><p>CarePass는 결제 수수료를 받지 않아요. 카드 실적과 포인트는 그대로 적립됩니다.</p></div>
      <p id="settlement-step-help" className="settlement-assistive">카드결제가 완료되면 송금 단계를 선택할 수 있어요.</p>
      <MemberActionBar>
        <Button size="large" disabled={query.isError} isLoading={payment.isPending}
          onClick={() => { setAgreed(false); setConfirming(true); }}>
          {money(transaction.approvedAmount ?? transaction.requestedAmount)} 카드로 결제
        </Button>
        <TransactionActions transaction={transaction} codes={["CANCEL_TRANSACTION"]} />
      </MemberActionBar>
      <Dialog open={confirming} title="카드로 결제할까요?" description="결제 금액과 받는 계좌를 확인해 주세요. 송금 방식은 결제 완료 후 선택합니다."
        className="settlement-payment-confirmation" closeDisabled={payment.isPending}
        onClose={() => { if (!submitting.current) { setConfirming(false); setAgreed(false); } }}
        secondaryAction={<Button variant="secondary" disabled={payment.isPending} onClick={() => { setConfirming(false); setAgreed(false); }}>취소</Button>}
        primaryAction={<Button disabled={!agreed || query.isError} isLoading={payment.isPending} onClick={() => {
          if (!submitting.current) { submitting.current = true; payment.mutate(); }
        }}>결제 진행</Button>}>
        <dl className="settlement-confirmation-details">
          <div><dt>결제 금액</dt><dd>{money(transaction.approvedAmount ?? transaction.requestedAmount)}</dd></div>
          <div><dt>받는 분</dt><dd>{transaction.recipientHolderMasked ?? "거래 상세에서 확인"}</dd></div>
          <div><dt>받는 계좌</dt><dd>{banks.data?.find(bank => bank.code === transaction.recipientBankCode)?.name ?? "은행"} · {transaction.recipientAccountNumberMasked}</dd></div>
        </dl>
        <label className="confirmation-check"><input type="checkbox" checked={agreed} onChange={event => setAgreed(event.currentTarget.checked)} />
          <span>승인 금액 {money(transaction.approvedAmount ?? transaction.requestedAmount)}을 확인했습니다.</span></label>
      </Dialog>
    </>}
    {!paid && !payable && !pending && !unknown && <><EmptyState title="현재 결제할 수 없는 거래입니다" description={transactionActionBlockedMessage(transaction, "PAY")}
      action={<Button onClick={() => navigate(transactionPath(`/app/transactions/${transactionId}`))}>거래 상세 보기</Button>} />
      <TransactionActions transaction={transaction} codes={["PAY"]} /></>}
    </div>
  </div>;
}
